import { useEffect, useMemo } from 'react'
import { useTheme, useMediaQuery } from '@mui/material'

/**
 * Returns a responsive page size and selector options tuned to the card-grid layout.
 *
 * @param {object} cols  – column count per MUI breakpoint, e.g. { xs:1, sm:2, md:3, lg:4 }
 * @param {object} [opts]
 * @param {number} [opts.targetRows=4] – preferred visible rows at sm+
 * @param {number} [opts.mobileRows=6] – visible rows on xs (single-column scroll)
 * @param {number} [opts.steps=3]      – how many multiples to offer in the selector
 * @param {function} [opts.onSizeChange] – called whenever the computed size changes
 * @returns {{ pageSize: number, options: number[] }}
 */
export const useResponsivePageSize = (
  cols = { xs: 1, sm: 2, md: 3, lg: 3 },
  { targetRows = 4, mobileRows = 6, steps = 3, onSizeChange } = {},
) => {
  const theme = useTheme()

  // Detect active breakpoint.
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const isSm = useMediaQuery(theme.breakpoints.only('sm'))
  const isMd = useMediaQuery(theme.breakpoints.only('md'))
  // lg and above fall through as the default

  const activeCols = isXs
    ? cols.xs
    : isSm
      ? cols.sm
      : isMd
        ? (cols.md ?? cols.sm)
        : (cols.lg ?? cols.md ?? cols.sm)

  const activeRows = isXs ? mobileRows : targetRows
  const pageSize = activeCols * activeRows

  // Build selector options: 1×, 2×, 3× the base page size, capped at 96.
  const options = useMemo(
    () =>
      Array.from({ length: steps }, (_, i) => Math.min(pageSize * (i + 1), 96)),
    [pageSize, steps],
  )

  // Notify caller (management hook) whenever the responsive size changes.
  useEffect(() => {
    onSizeChange?.(pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize])

  return { pageSize, options }
}
